import { Box, Container, createStyles, Drawer, IconButton, makeStyles, Theme } from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import { HELP_ROUTE } from 'features/help/constants';
import { ContentfulErrorContext, ContentfulErrorTypes } from 'features/help/hooks/ContentfulErrorContext';
import { useMobileNav } from 'features/help/hooks/MobileNavContext';
import { TopicCollection } from 'features/help/types';
import React, { ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTopicsWithArticlesQuery } from '../../../queries/useTopicsWithArticlesQuery';
import { Error } from '../Error/Error';
import { NoContent } from '../Error/NoContent';
import { HelpFooter } from '../Footer/HelpFooter';
import { HelpHeader } from '../Header/HelpHeader';
import { TopicPageHeader } from '../Header/TopicPageHeader';
import { LeftHandNav } from '../LeftHandNav';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    page: {
      minHeight: 'fit-content',
      width: '100%',
      flex: '1',
      display: 'flex',
      flexDirection: 'column',
      margin: 0,
    },
    container: {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
    },
    pageContentArea: {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    navAndContent: {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'row',
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(4),
      [theme.breakpoints.up('md')]: {
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(8),
      },
    },
    leftHandNavContainer: {
      display: 'none',
      minWidth: 256,
      flexDirection: 'column',
      alignItems: 'flex-start',
      alignSelf: 'stretch',
      borderRight: `1px solid ${theme.palette.divider}`,
      marginRight: theme.spacing(4),
      paddingRight: theme.spacing(4),
      paddingTop: theme.spacing(1),
      [theme.breakpoints.up('md')]: {
        display: 'flex',
      },
    },
    bodyContent: {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    drawerPaper: {
      width: 280,
      padding: theme.spacing(2),
    },
    drawerHeader: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginBottom: theme.spacing(1),
    },
  }),
);

export function HelpLayoutContent({ children }: { children: ReactNode }) {
  const classes = useStyles();
  const [topics, setTopics] = useState<TopicCollection | null>();
  const { mobileNavOpen, setMobileNavOpen, isMobile } = useMobileNav();

  const location = useLocation();
  const [path, setPath] = useState<string>(location.pathname.replaceAll('/', ''));

  const { contentfulErrorType, setContentfulErrorType } = React.useContext(ContentfulErrorContext);

  const helpHomePage = `${HELP_ROUTE.replaceAll('/', '')}`;
  const isHelpHomePage = path === helpHomePage;

  useEffect(() => {
    const fetchData = async () => {
      const { data, error } = await useTopicsWithArticlesQuery();

      setTopics(data);

      if (error) {
        setContentfulErrorType(ContentfulErrorTypes.Error);
      }

      if (!data) {
        setContentfulErrorType(ContentfulErrorTypes.NoContent);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    setPath(location.pathname.replaceAll('/', ''));
  }, [location]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname, setMobileNavOpen]);

  const handleDrawerClose = () => {
    setMobileNavOpen(false);
  };

  return (
    <>
      <div className={classes.page}>
        {isHelpHomePage ? <HelpHeader /> : <TopicPageHeader topics={topics} />}
        <div className={classes.pageContentArea}>
          {contentfulErrorType ? (
            <>
              {contentfulErrorType === ContentfulErrorTypes.Error && <Error />}
              {contentfulErrorType === ContentfulErrorTypes.NoContent && <NoContent />}
            </>
          ) : (
            <>
              {isHelpHomePage ? (
                children
              ) : (
                <Container className={classes.container} maxWidth={'lg'}>
                  <>
                    <div className={classes.navAndContent}>
                      {topics && (
                        <div className={classes.leftHandNavContainer}>
                          <LeftHandNav topics={topics} />
                        </div>
                      )}
                      <div className={classes.bodyContent}>{children}</div>
                    </div>
                  </>
                </Container>
              )}
            </>
          )}
        </div>
        <HelpFooter isHomePage={isHelpHomePage} />
      </div>

      {isMobile && topics && (
        <Drawer
          variant="temporary"
          anchor="left"
          open={mobileNavOpen}
          onClose={handleDrawerClose}
          classes={{ paper: classes.drawerPaper }}
          ModalProps={{ keepMounted: true }}
        >
          <Box className={classes.drawerHeader}>
            <IconButton
              onClick={handleDrawerClose}
              aria-label="Close navigation menu"
              data-testid="mobile-nav-close-button"
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <LeftHandNav topics={topics} />
        </Drawer>
      )}
    </>
  );
}
